import { Vestiging } from "@/entities/vestiging";
import { VerlengingStatus, VestigingStatus } from "@/entities/vestiging/constants";
import { ActionsDropdown, Badge, DropdownMenuItem, Icon, Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components";
import { useIntervalRerender } from "@/shared/hooks";
import { useState } from "react";
import { FaciliteitenVariant, VerlengenOpeningstijdenDialog, VerzoekVerlengenOpeningstijdenDialog } from "../../shared";
import { StopFaciliteitenDialog } from "./stop-faciliteiten-dialog";

export type Handlers = {
    onStopRequest: (vestiging: Vestiging) => Promise<void>;
    onUpdateOpeningstijd: (vestiging: Vestiging, openTot: string) => Promise<void>;
};

const badgeMap: Record<VestigingStatus, React.ReactElement> = {
    [VestigingStatus.BESTELD]: (
        <Badge variant="blue-subtle" className="text-xs">
            Besteld
        </Badge>
    ),
    [VestigingStatus.ACTIEF]: (
        <Badge variant="green-subtle" className="text-xs">
            Actief
        </Badge>
    ),
    [VestigingStatus.STOP_AANGEVRAAGD]: (
        <Badge variant="yellow-subtle" className="text-xs">
            Stop aangevraagd
        </Badge>
    ),
    [VestigingStatus.GESTOPT]: (
        <Badge variant="red-subtle" className="text-xs">
            Gestopt
        </Badge>
    ),
    [VestigingStatus.GAAT_NIET_LEVEREN]: (
        <Badge variant="red-subtle" className="text-xs">
            Gaat niet leveren
        </Badge>
    ),
};

type BadgeColProps = {
    vestiging: Vestiging;
};

export function BadgeCol({ vestiging }: Readonly<BadgeColProps>) {
    useIntervalRerender(60); // re-render every minute so minutesUntilClose is live updating

    const minutesUntilClose = Vestiging.getMinutesUntilClose(vestiging);
    const closesSoon = minutesUntilClose != null && minutesUntilClose <= 65;
    const openingstijdenStatus = vestiging.currentOpeningstijden.aangepasteOpenTotStatus;

    const openingstijdAangevraagd =
        (vestiging.aangevraagdeVestiging?.status === VestigingStatus.BESTELD ||
            vestiging.aangevraagdeVestiging?.status === VestigingStatus.ACTIEF) &&
        openingstijdenStatus === VerlengingStatus.AANGEVRAAGD;
    const openingstijdAangepast = openingstijdenStatus === VerlengingStatus.ACTIEF && vestiging.currentOpeningstijden.aangepasteOpenTot;
    const openingstijdAfgewezen =
        vestiging.aangevraagdeVestiging?.status === VestigingStatus.ACTIEF && openingstijdenStatus === VerlengingStatus.AFGEWEZEN;

    return (
        <div className="flex items-center justify-end gap-4 pr-0.5">
            {closesSoon && (
                <div className="mr-auto flex items-center gap-2">
                    <Icon name="alert" className="text-content-emphasis" />
                    <p className="text-content-subtle text-s" data-testid="openingstijd-sluit-snel">
                        Let op, sluit over {minutesUntilClose} minuten
                    </p>
                </div>
            )}

            {!closesSoon && openingstijdAangevraagd && (
                <p className="text-s text-content-subtle mr-auto" data-testid="openingstijd-aangevraagd">
                    Openingstijd aangevraagd: {vestiging.currentOpeningstijden.aangepasteOpenTot}
                </p>
            )}

            {!closesSoon && openingstijdAangepast && (
                <p className="text-s text-content-subtle mr-auto" data-testid="openingstijd-aangepast">
                    Openingstijd aangepast
                </p>
            )}

            {!closesSoon && openingstijdAfgewezen && (
                <p className="text-s text-content-subtle mr-auto" data-testid="openingstijd-afgewezen">
                    Openingstijd afgewezen
                </p>
            )}

            {vestiging.aangevraagdeVestiging!.toelichtingKlantenservice && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Icon name="comment" alt="feedback klantenservice" className="text-content-emphasis" />
                    </TooltipTrigger>
                    <TooltipContent>
                        <p className="flex flex-col">
                            <span>Toelichting klantenservice:</span>
                            <span>"{vestiging.aangevraagdeVestiging!.toelichtingKlantenservice}"</span>
                        </p>
                    </TooltipContent>
                </Tooltip>
            )}
            {vestiging.aangevraagdeVestiging?.status ? (badgeMap[vestiging.aangevraagdeVestiging?.status] ?? null) : null}
        </div>
    );
}

type MenuColProps = Handlers & {
    variant?: FaciliteitenVariant;
    vestiging: Vestiging;
};

export function MenuCol({ vestiging, onStopRequest, onUpdateOpeningstijd }: Readonly<MenuColProps>) {
    const [stopFaciliteitenDialogOpen, setStopFaciliteitenDialogOpen] = useState(false);
    const [verlengenOpeningstijdenDialogOpen, setVerlengenOpeningstijdenDialogOpen] = useState(false);

    const handleStopConfirm = async () => {
        await onStopRequest(vestiging);
        setStopFaciliteitenDialogOpen(false);
    };

    const handleVerlengOpeningstijden = async (vestiging: Vestiging, openTot: string) => {
        await onUpdateOpeningstijd(vestiging, openTot);
        setStopFaciliteitenDialogOpen(false);
    };

    return (
        <>
            <ActionsDropdown data-testid="vestiging-bestaande-aanvraag">
                {Vestiging.canModifyOpeningHours(vestiging, "bestaandeAanvraag") && (
                    <DropdownMenuItem onClick={() => setVerlengenOpeningstijdenDialogOpen(true)}>Openingstijd verlengen</DropdownMenuItem>
                )}
                {Vestiging.canRequestStop(vestiging) && (
                    <DropdownMenuItem onClick={() => setStopFaciliteitenDialogOpen(true)}>Verzoek stoppen faciliteiten</DropdownMenuItem>
                )}
            </ActionsDropdown>
            <StopFaciliteitenDialog
                open={stopFaciliteitenDialogOpen}
                onOpenChange={setStopFaciliteitenDialogOpen}
                variant={{ type: "vestiging", vestiging }}
                onConfirm={() => void handleStopConfirm()}
                onCancel={() => setStopFaciliteitenDialogOpen(false)}
            />
            {vestiging.beheerVestiging.isNsRetailVestiging ? (
                <VerlengenOpeningstijdenDialog
                    vestiging={vestiging}
                    onCancel={() => setVerlengenOpeningstijdenDialogOpen(false)}
                    onUpdateOpeningstijd={handleVerlengOpeningstijden}
                    onOpenChange={setVerlengenOpeningstijdenDialogOpen}
                    open={verlengenOpeningstijdenDialogOpen}
                    key={
                        verlengenOpeningstijdenDialogOpen
                            ? `${vestiging.aangevraagdeVestiging!.id}-open`
                            : `${vestiging.aangevraagdeVestiging!.id}-closed`
                    } // this key makes sure we don't see stale form data / validation messages when re-opening the dialog
                />
            ) : (
                <VerzoekVerlengenOpeningstijdenDialog
                    vestiging={vestiging}
                    onCancel={() => setVerlengenOpeningstijdenDialogOpen(false)}
                    onUpdateOpeningstijd={handleVerlengOpeningstijden}
                    onOpenChange={setVerlengenOpeningstijdenDialogOpen}
                    open={verlengenOpeningstijdenDialogOpen}
                    key={
                        verlengenOpeningstijdenDialogOpen
                            ? `${vestiging.aangevraagdeVestiging!.id}-open`
                            : `${vestiging.aangevraagdeVestiging!.id}-closed`
                    } // this key makes sure we don't see stale form data / validation messages when re-opening the dialog
                />
            )}
        </>
    );
}
